import React, {useState, useEffect} from 'react';
import { Row, Col, Card, Form, Button } from "react-bootstrap";
import { useOrders } from "../OrderContext";
import { usePurchases } from "../PurchaseContext";
// import { calculatedOrderProfit } from "./orders";
import { useNavigate } from "react-router-dom";
// import necessary components from react-chartjs-2
import { Line, Bar } from 'react-chartjs-2';
import { FaChartLine, FaChartBar } from "react-icons/fa";
import { AccountingScopeSelect, useAccountingScope } from "./Accounting/accountingscope";
// import the necessary components from Chart.js
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale } from 'chart.js';
// register the necessary chart.js components
ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale);


function Reports() {
    const scope = useAccountingScope();
    const { orders, fetchAllOrders } = useOrders();
    const { purchases, fetchAllPurchasesAll } = usePurchases();
    const [ startDate, setStartDate ] = useState('');
    const [ endDate, setEndDate ] = useState('');
    const [graphType, setGraphType] = useState('line'); // or 'bar'

    const navigate = useNavigate();

    // useEffect(() => {
    //     fetchOrders();
    //     fetchAllPurchases();
    // }, [fetchOrders, fetchAllPurchases]);

    useEffect(() => {
        // if no chosen date range, default to current month/day, let's go with day for now
        const today = new Date().toISOString().split('T')[0];
        setStartDate(today);
        setEndDate(today);
    }, []);

    useEffect(() => {
        if (startDate && endDate && !scope.loadingScope && scope.storeId) {
            // fetch orders for that date range
            fetchAllOrders({
                start_date: startDate,
                end_date: endDate,
                store_id: scope.storeId,
            });
            fetchAllPurchasesAll({ start_date: startDate, end_date: endDate });
        }
    }, [startDate, endDate, scope.loadingScope, scope.storeId, fetchAllOrders, fetchAllPurchasesAll]);

    const selectedStore = scope.stores.find((store) => String(store.id) === String(scope.storeId))
        || (String(scope.currentStore?.id) === String(scope.storeId) ? scope.currentStore : null);
    const showCentralPurchaseMetrics = scope.storeId === "all" || selectedStore?.store_type === "WAREHOUSE";


    // filter orders by date range
    const filteredOrders = orders.filter(order => {
        const orderDate = new Date(order.created_at);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(`${endDate}T23:59:59.999`) : null;

        return (
            (!start || orderDate >= start) &&
            (!end || orderDate <= end)
        );
    });

    // filter purchases by date range
    const filteredPurchases = purchases.filter(purchase => {
        const purchaseDate = new Date(purchase.purchase_date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(`${endDate}T23:59:59.999`) : null;

        return (
            (!start || purchaseDate >= start) &&
            (!end || purchaseDate <= end)
        );
    });

    // calculate total purchases
    const totalPurchases = filteredPurchases.reduce((total, purchase) => {
        return total + parseFloat(purchase.total_amount || 0);
    } , 0);

    // calculate total purchase returns
    const totalPurchaseReturns = filteredPurchases.reduce((total, purchase) => {
        return total + parseFloat(purchase.return_amount || 0);
    }, 0);

    // calculate total purchases (net purchases)
    const netPurchases = totalPurchases - totalPurchaseReturns;

    // calculate total number of purchases
    const numberOfPurchases = filteredPurchases.length;

    // calculate total discounts
    const totalDiscounts = filteredOrders.reduce((total, order) => {
        return total + parseFloat(order.discount || 0);
    }, 0);

    // total sales, gross sales and returns calculation for the grid
    const grossSales = filteredOrders.reduce((total, order) => {
        return total + parseFloat(order.total_amount || 0);
    }, 0);

    // calculate total returns
    const totalReturns = filteredOrders.reduce((total, order) => {
        const returns = order.items.reduce((itemTotal, item) => {
            const unitPrice = parseFloat(item.unit_price) || 0;
            const returnedQuantity = parseFloat(item.returned_quantity) || 0;
            return itemTotal + (unitPrice * returnedQuantity);
        }, 0);
        return total + returns;
    }, 0);

    // calculate total sales (gross sales - returns)
    const totalSales = grossSales - totalDiscounts - totalReturns;

    // daily average sales
    const daySpan = (startDate && endDate)
        ? (new Date(endDate) - new Date(startDate)) / 86_400_000 + 1 // ms->days +1 so same-day = 1
        : 1; // if no date range, assume 1 day to avoid division by zero
        //  86,400,000 ms in a day
        // new Date(endDate) - new Date(startDate) gives the difference in milliseconds dividing by 86_400_000 converts it to days
        // +1 is to include the start date in the count
    const dailyAverageSales = daySpan ? totalSales / daySpan : 0;

    // number of orders
    const numberOfOrders = filteredOrders.length || 0;

    // calculate value of products sold (sum of(unit price * quantity sold))
    const valueOfProductsSold = filteredOrders.reduce((total, order) => {
        return total + order.items.reduce((itemTotal, item) => {
            const unitPrice = parseFloat(item.unit_price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            return itemTotal + (unitPrice * quantity);
        }, 0);
    }, 0);

    // total profit
    // const totalProfit = filteredOrders.reduce((profit, order) => {
    //     return profit + parseFloat(calculatedOrderProfit(order));
    // }, 0);
    //
    // // profit percentage, ensure totalSales is not 0 to avoid division by zero
    // const profitPercentage = totalSales > 0 ? (totalProfit / totalSales) * 100 : 0;

    // added for total profit and profit percentage calculation
    // net sales: sum of final_amount (already net of discounts and returns)
    const totalNetSales = filteredOrders.reduce((total, order) => total + (parseFloat(order.final_amount) || 0), 0);

    // COGS: cost_price * (qty -returned_qty)
    const totalCOGS = filteredOrders.reduce((total, order) => {
        const cogs = order.items.reduce((itemTotal, item) => {
            const netQty =
                Math.max((parseFloat(item.quantity) || 0) - (parseFloat(item.returned_quantity) || 0), 0);
            return itemTotal + ((parseFloat(item.cost_price) || 0) * netQty);
        }, 0);
        return total + cogs;
    }, 0);

    // gross profit aligned with income statement
    const totalProfit = totalNetSales - totalCOGS;

    // profit percentage
    const profitPercentage = totalNetSales > 0 ? (totalProfit / totalNetSales) * 100 : 0;
    // end of added

    // average order value
    const averageOrderValue = numberOfOrders > 0 ? (totalSales / numberOfOrders) : 0;

    // top-selling product
    // add top-selling product by value later
    const productSales = {};
    filteredOrders.forEach(order => {
        order.items.forEach(item => {
            const productId = item.product_details?.id; //<-- null-safe
            if (productId) {
                const quantity = parseFloat(item.quantity) || 0;
                productSales[productId] = (productSales[productId] || 0) + quantity;
            }
        });
    });
    const topProductId = Object.keys(productSales).reduce((a, b) =>
        productSales[a] > productSales[b] ? a : b, null);
    const topProduct = topProductId ? orders.find(order => order.items.some(item =>
        item.product_details.id === parseInt(topProductId))).items.find(item =>
        item.product_details.id === parseInt(topProductId)).product_details.product_name : 'N/A';

    // added for VAT payable
    // Sales VAT calculation
    const VAT_RATE = 0.16; // 16% VAT rate

    const calculateOrderVat = (order) => {
        return order.items.reduce((total, item) => {
            const unitPrice = parseFloat(item.unit_price) || 0;
            const qty = parseFloat(item.quantity) || 0;
            const returnedQty = parseFloat(item.returned_quantity) || 0;
            const applicable = item.product_details?.is_vat_applicable;

            if (!applicable) return total; // skip non-VAT items

            // gross (VAT inclusive) totals
            const soldTotal = unitPrice * qty;
            const returnedTotal = unitPrice * returnedQty;

            // extract VAT from VAT-inclusive prices
            const soldVat = soldTotal - (soldTotal / (1 + VAT_RATE));
            const returnedVat = returnedTotal - (returnedTotal / (1 + VAT_RATE));
            return total + (soldVat - returnedVat);
        }, 0);
    };

    const totalVatPayable = filteredOrders.reduce((total, order) => {
        return total + calculateOrderVat(order);
    }, 0);

    // Purchase VAT calculation
    const calculatePurchaseVat = (purchase) => {
        return purchase.items.reduce((total, item) => {
            const unitCost = parseFloat(item.purchase_price) || 0;
            const qty = parseFloat(item.quantity) || 0;
            const applicable = item.is_vat_applicable;

            if (!applicable) return total; // skip non-VAT items

            // VAT-inclusive amount
            const itemTotal = unitCost * qty;

            // extract VAT portion
            const preTax = itemTotal / (1 + VAT_RATE);
            const vatAmount = itemTotal - preTax;

            // handle purchase returns if any
            const returnedQty = parseFloat(item.returned_quantity) || 0;
            if (returnedQty > 0) {
                const returnedTotal = unitCost * returnedQty;
                const returnedPreTax = returnedTotal / (1 + VAT_RATE);
                const returnedVat = returnedTotal - returnedPreTax;
                return total + (vatAmount - returnedVat);
            }
            return total + vatAmount;
        }, 0);
    };

    const totalPurchaseVat = filteredPurchases.reduce((total, purchase) => {
        return total + calculatePurchaseVat(purchase);
    }, 0);

    const finalVatPayable = totalVatPayable - totalPurchaseVat;
    // end of added

    // added replacing 'prepare data for the month' above
    // for graphs
    // --- build an array of all dates in the chosen range ---
    const dateList = [];
    if (startDate && endDate) {
        for (let d = new Date(startDate);
             d <= new Date(endDate);
             d.setDate(d.getDate() + 1)) {
            dateList.push(new Date(d));
        }
    }

    // labels like 1 April, 2 April, etc.
    const labels = dateList.map(d =>
        d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    );

    // aggregate sales & order-count per day
    const salesByDay = Object.create(null);
    const ordersByDay = Object.create(null);

    filteredOrders.forEach(o => {
        const key = new Date(o.created_at).toISOString().slice(0, 10); // YYYY-MM-DD
        salesByDay[key] = (salesByDay[key] || 0) + parseFloat(o.final_amount || 0);
        ordersByDay[key] = (ordersByDay[key] || 0) + 1;
    });

    // map totals back to the label order
    // both below are hash-maps, so we can use the dateList to get the totals for each day
    const salesData = dateList.map(d => salesByDay[d.toISOString().slice(0, 10)] || 0);
    const ordersData = dateList.map(d => ordersByDay[d.toISOString().slice(0, 10)] || 0);
    // end of added

    return (
        <>
            <h1>Reports</h1>
            <Form className="mb-4">
                <Row>
                    {scope.isAdmin && (
                        <Col md={4}>
                            <AccountingScopeSelect scope={scope} label="Report Scope"/>
                        </Col>
                    )}
                    <Col md={3}>
                        <Form.Group controlId="startDate">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="endDate">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Form>

            <Row className="mb-4">
            {/* first row */}
                <Col md={3}>
                    <Card className="text-center bg-info">
                        <Card.Body>
                            <Card.Title>Total Sales</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalSales.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                            })}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center bg-secondary">
                        <Card.Body>
                            <Card.Title>Gross Sales</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{grossSales.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center bg-danger" style={{ cursor: 'pointer' }} onClick={() => navigate("/orders/returns")}>
                        <Card.Body>
                            <Card.Title>Order Returns</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalReturns.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center bg-warning" style={{ cursor: 'pointer' }} onClick={() => navigate("/orders")}>
                        <Card.Body>
                            <Card.Title>Number of Orders</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{numberOfOrders}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>

                {/*second row*/}
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-success" style={{ cursor: 'pointer' }} onClick={() => navigate("/reports/products")}>
                        <Card.Body>
                            <Card.Title>Value of Products Sold</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{valueOfProductsSold.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-body-tertiary">
                        <Card.Body>
                            <Card.Title>Total Profit</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalProfit.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-primary">
                        <Card.Body>
                            <Card.Title>Profit Percentage</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                                {profitPercentage.toLocaleString(undefined, { maximumFractionDigits: 2 })}%
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-info">
                        <Card.Body>
                            <Card.Title>Average Order Value</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{averageOrderValue.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                            })}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-light">
                        <Card.Body>
                            <Card.Title>Daily Average Sales</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                                {dailyAverageSales.toLocaleString(undefined, {maximumFractionDigits: 0})}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-secondary">
                        <Card.Body>
                            <Card.Title>Top Selling Product</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold' }}>{topProduct}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-danger">
                        <Card.Body>
                            <Card.Title>Total Discounts</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalDiscounts.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                {showCentralPurchaseMetrics && (
                    <>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-warning">
                                <Card.Body>
                                    <Card.Title>Net Purchases</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{netPurchases.toLocaleString()}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-success" style={{ cursor: 'pointer' }} onClick={() => navigate("/inventory/purchases")}>
                                <Card.Body>
                                    <Card.Title>Number of Purchases</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{numberOfPurchases}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-info">
                                <Card.Body>
                                    <Card.Title>Sales VAT</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalVatPayable.toLocaleString()}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-secondary">
                                <Card.Body>
                                    <Card.Title>Purchase VAT</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalPurchaseVat.toLocaleString()}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-danger">
                                <Card.Body>
                                    <Card.Title>VAT Payable</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                                        {finalVatPayable.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}
            </Row>

            {/*icons to switch graph types*/}
            <div className="d-flex justify-content-end mb-3">
                <Button variant={graphType === 'line' ? 'primary' : 'secondary'}
                        onClick={() => setGraphType('line')} className="button-spacing">
                    <FaChartLine size={20} />
                </Button>
                <Button variant={graphType === 'bar' ? 'primary' : 'secondary'}
                        onClick={() => setGraphType('bar')}>
                    <FaChartBar size={20} />
                </Button>

                {/*{graphType === 'line' ? (*/}
                {/*    <Line key="line-chart" data={chartData} />*/}
                {/*) : (*/}
                {/*    <Bar key="bar-chart" data={chartData} />*/}
                {/*)}*/}
            </div>

            {/* graph */}
            <Row>
                <Col md={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Monthly Net Sales</Card.Title>
                            {graphType === 'line' ? (
                                <Line
                                    key="line-chart"  // add a unique key to the chart to force a re-render of chart type
                                    data={{
                                        labels,
                                        datasets: [{
                                            label: 'Net Sales',
                                            data: salesData,
                                            backgroundColor: 'rgba(75, 192, 192, 0.4)',
                                            borderColor: 'rgba(75, 192, 192, 1)',
                                            fill: false,
                                        }]
                                    }}
                                />
                            ) : (
                                <Bar
                                    key="bar-chart"  // add a unique key to the chart to force a re-render of chart type
                                    data={{
                                        labels,
                                        datasets: [{
                                            label: 'Net Sales',
                                            data: salesData,
                                            backgroundColor: 'rgba(75, 192, 192, 0.4)',
                                        }]
                                    }}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Monthly Orders</Card.Title>
                            {graphType === 'line' ? (
                                <Line data={{
                                    labels,
                                    datasets: [{
                                        label: 'Number of Orders',
                                        data: ordersData,
                                        backgroundColor: 'rgba(153, 102, 255, 0.4)',
                                        borderColor: 'rgba(153, 102, 255, 1)',
                                        fill: false,
                                    }]
                                }} />
                            ) : (
                                <Bar data={{
                                    labels,
                                    datasets: [{
                                        label: 'Orders',
                                        data: ordersData,
                                        backgroundColor: 'rgba(153, 102, 255, 0.4)',
                                    }]
                                }} />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

        </>
    )
}

export default Reports;